import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import type { IconDictionary } from "../Icon/types";

import { ChatMessage, ChatMessageProps } from "./ChatMessage";
import styles from "./ChatWindow.scss";

export type UserLike = {
  id: string;
  image70?: string | null;
  name?: string | React.ReactNode;
  initials?: string;
};
export type OnMentionCallback = (results: UserLike) => void;
export type OnEditCallback = (args: { id: string; message: string }) => void;
export type OnDeleteCallback = (id: string) => void;
export type RenderChatMessageFunction = (args: {
  /**
   * UserId for currently logged-in user. The same value passed through from ChatWindow.
   */
  currentUserId?: string;
  /**
   * Object for custom i18n text for all labels
   */
  labels: ChatWindowProps["labels"];
  /**
   * True if it's the first message a user is posting in a string of texts (and should show their profile image)
   */
  firstMessage: boolean;
  /**
   * The message object
   */
  message: ChatMessageProps["message"];
  /**
   * The same function passed through from ChatWindow
   */
  onDelete?: OnDeleteCallback;
  /**
   * The same function passed through from ChatWindow
   */
  onEdit?: OnEditCallback;
  /**
   * The user who is speaking
   */
  user: UserLike;
}) => any;

export interface ChatWindowProps {
  /**
   * Function that handles what to do when a user wants to edit. Takes the full message as an argument.
   */
  onEdit?: OnEditCallback;
  /**
   * Function that handles what to do when a user wants to delete a message. Takes the full message as the single argument.
   */
  onDelete?: (id: string) => void;
  /**
   * Function that handles what to do when a user creates a mention in the edit modal
   */
  onMention?: (searchTerm: string, callback: OnMentionCallback) => void;
  /**
   * CSS class name for ChatWindow component
   */
  className?: string;
  /**
   * All messages included in the chat.
   */
  messages?: ChatMessageProps["message"][];
  /**
   * All users who have messages included in the chat. Either this or `userMap` are required to be set.
   */
  users?: UserLike[];
  /**
   * All users who have a message in the chat. Either this or the `users` array are required to be set.
   */
  userMap?: Record<string, UserLike>;
  /**
   * UserId for currently logged-in user. Used to designate the current viewer to highlight their own messages.
   */
  currentUserId?: string;
  /**
   * Function to render custom chat messages.
   */
  renderChatMessage?: RenderChatMessageFunction;
  /**
   * Object for custom internationalization text for all labels.
   */
  labels?: {
    closeAriaLabel?: string;
    deleteIcon?: string;
    deleteHeader?: string;
    deleteConfirmation?: string;
    deleteSaveButton?: string;
    deleteCancelButton?: string;
    editIcon?: string;
    editHeader?: string;
    editSaveButton?: string;
    editCancelButton?: string;
    mentionsPlaceHolder?: string;
  };
  displayMode?: "default" | "compressed";
  /**
   * If provided all user icons will render as this icon instead of the profile pictures or initials.
   */
  profilePicIconOverride?: IconDictionary;
  linkElement?: React.ComponentType<any>;
}

/**
 * A chat component that takes messages and users and displays them like a chat
 * window. Note that the component will not format any dates or message details
 * for you. Message content can be either strings or React components.
 *
 * You must manage the state of the chat user and message data via your own
 * component.
 *
 * Message content can be either strings or React components/nodes to allow for
 * rich text.
 */
export function ChatWindow({
  className,
  currentUserId,
  messages = [],
  onDelete,
  onEdit,
  onMention,
  renderChatMessage,
  users,
  userMap,
  labels,
  displayMode,
  profilePicIconOverride,
  linkElement,
}: ChatWindowProps) {
  const internalUserMap = React.useMemo(() => {
    const internalMap: Record<string, UserLike> = coalesce(userMap, {});
    if (users) {
      users.forEach((user) => (internalMap[user.id] = user));
    }
    return internalMap;
  }, usersAndUsersMapDependencyArray(users, userMap));
  const chatWindowClassName = cx(styles["chat-window"], className);

  return (
    <div className={chatWindowClassName}>
      <div>
        {messages.map((message, i) => {
          const user = internalUserMap[message.userId];
          return (
            <React.Fragment key={message.id}>
              {renderChatMessage ? (
                renderChatMessage({
                  currentUserId,
                  message,
                  onDelete,
                  onEdit,
                  user,
                  labels,
                  firstMessage: i === 0,
                })
              ) : (
                <ChatMessage
                  currentUserId={currentUserId}
                  message={message}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onMention={onMention}
                  user={user}
                  labels={labels}
                  displayMode={displayMode}
                  profilePicIconOverride={profilePicIconOverride}
                  linkElement={linkElement}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function usersAndUsersMapDependencyArray(
  users: UserLike[] | undefined,
  userMap: Record<string, UserLike> | undefined
): unknown[] {
  const usersArrayString = users?.map((user) => user.id).join(",");
  const userMapArrayString = Object.keys(coalesce(userMap, {})).join(",");
  return [usersArrayString, userMapArrayString];
}
